import { createTheme, type Theme } from '@mui/material/styles'
import { getFieldSurfaceStyle } from '@/components/common/formFields/fieldSurfaces/registry'
import type { FieldSurface } from '@/components/common/formFields/fieldSurfaces/types'

/**
 * Theme MUI OutlinedInput/Select theo surface — form | filterToolbar | filterSidebar | pos | posUnderline.
 */
export function createKiotFieldSurfaceTheme(baseTheme: Theme, surface: FieldSurface = 'form') {
  const k = getFieldSurfaceStyle(surface)
  const underline = (k.inputChrome ?? 'box') === 'underline'
  const borderWidth = underline ? '0 0 1px 0' : 1
  const focusBorderWidth = underline ? '0 0 2px 0' : 1
  const padY = Math.round((k.inputHeightPx - k.inputFontSizePx * k.lineHeight) / 2)
  return createTheme(baseTheme, {
    shape: {
      borderRadius: k.borderRadiusPx,
    },
    typography: {
      fontFamily: k.fontFamily,
      body1: {
        fontFamily: k.fontFamily,
        fontSize: `${k.inputFontSizePx}px`,
        lineHeight: k.lineHeight,
        color: k.inputColor,
      },
    },
    components: {
      MuiFormLabel: {
        styleOverrides: {
          root: {
            fontFamily: k.fontFamily,
            fontSize: `${k.labelFontSizePx}px`,
            fontWeight: k.labelFontWeight,
            color: k.labelColor,
            opacity: 1,
            '&.Mui-focused': { color: k.labelColor },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontFamily: k.fontFamily,
            fontSize: `${k.labelFontSizePx}px`,
            fontWeight: k.labelFontWeight,
            color: k.labelColor,
          },
          outlined: {
            '&.MuiInputLabel-shrink': {
              backgroundColor: underline ? 'transparent' : k.inputBg,
              paddingLeft: 4,
              paddingRight: 4,
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ ownerState }) => ({
            fontFamily: k.fontFamily,
            fontSize: `${k.inputFontSizePx}px`,
            borderRadius: `${k.borderRadiusPx}px`,
            backgroundColor: k.inputBg,
            minHeight: ownerState.multiline ? 'unset' : k.inputHeightPx,
            alignItems: ownerState.multiline ? 'flex-start' : undefined,
            // MUI mặc định multiline: padding 16.5px 14px trên root — gây ô cao, lệch ô text.
            ...(ownerState.multiline ? { padding: 0 } : {}),
            ...(underline ? { boxShadow: 'none' } : {}),
            '& fieldset': {
              borderColor: k.borderColor,
              borderWidth,
              ...(underline ? { borderStyle: 'solid', borderRadius: 0 } : {}),
            },
            '&:hover fieldset': {
              borderColor: k.borderHover,
              ...(underline ? { borderWidth } : {}),
            },
            '&.Mui-focused fieldset': {
              borderColor: k.focusColor,
              borderWidth: focusBorderWidth,
            },
            '&.Mui-focused': {
              boxShadow: k.focusShadow,
            },
          }),
          input: {
            padding: `${padY}px ${k.inputPaddingX}px`,
            fontSize: `${k.inputFontSizePx}px`,
            fontWeight: k.inputFontWeight,
            lineHeight: k.lineHeight,
            color: k.inputColor,
            height: 'auto',
            boxSizing: 'border-box',
          },
          inputSizeSmall: {
            padding: `${padY}px ${k.inputPaddingX}px`,
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            padding: underline ? `8px 28px 8px ${k.inputPaddingX}px` : '8px 36px 8px 12px',
            minHeight: `${k.inputHeightPx - 16}px`,
          },
        },
      },
    },
  })
}

/** @deprecated — dùng `createKiotFieldSurfaceTheme(base, 'form')`. */
export function createKiotFormTheme(baseTheme: Theme) {
  return createKiotFieldSurfaceTheme(baseTheme, 'form')
}
